import React, { useEffect, useState } from 'react';
import { View, ScrollView, Text, StyleSheet, TouchableOpacity, Pressable } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import { getReservationDetail, getTransactionsData } from '../../../api/Reservation';

interface Props {
  reservationId: number;
  openAddTransactionModal: ()=>void;
  openRefundModal: (refundDetails)=>void;
}

const TransactionList = ({reservationId, openAddTransactionModal, openRefundModal}:Props) => {
  const [reservationInfo, setReservationInfo] = useState<any>({});
  const [transactionData, setTransactionData] = useState([]);

  useEffect(()=>{
    getTransactionsData({reservation_id:reservationId}, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          if (jsonRes && jsonRes.length > 0) console.log('[TransactionList] first transaction keys:', Object.keys(jsonRes[0]), jsonRes[0]);
          setTransactionData(jsonRes);
          break;
        default:
          setTransactionData([]);
          break;
      }
    });
  }, [reservationId, openAddTransactionModal])

  useEffect(() => {
    if (reservationId) {
      getReservationDetail(reservationId, (jsonRes, status, error) => {
        switch (status) {
          case 200:
            if(jsonRes.total_price == 0){
              jsonRes.total_price = jsonRes.subtotal + jsonRes.tax_amount - jsonRes.discount_amount;
            }
            setReservationInfo(jsonRes);
            break;
          default:
            setReservationInfo({});
            break;
        }
      });
    }
  }, [reservationId, openAddTransactionModal]);

  const renderItems = () => {
    return transactionData.map((transaction) => (
      <View key={transaction.id} style={styles.transactionItem}>
        <View style={{flexDirection:'row', justifyContent:'space-between', marginBottom:8}}>
          <Text>{reservationInfo?.customer?.email}</Text>
          <Text>{(() => {
            const raw = transaction.created_at || transaction.createdAt;
            if (!raw) return '-';
            const d = new Date(raw.replace(' ', 'T'));
            return isNaN(d.getTime()) ? raw : d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
          })()}</Text>
        </View>
        <View style={{flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start'}}>
          <View style={{flexDirection:'row', alignItems:'center', justifyContent:'space-between'}}>
            <Text>{transaction.method}</Text>
            {transaction.method && transaction.method.toLowerCase() == 'stripe' && (() => {
              // Fees are tracked PER TRANSACTION on the payment row (service_fee +
              // bodhi_protect_price). A transaction is refundable only for the amount
              // left after protecting its own non-refundable fees.
              const thisChargeRemaining = Math.max(0, parseFloat(transaction.amount || 0) - (parseFloat(transaction.refunded) || 0));
              const txnFees = parseFloat(transaction.service_fee || 0) + parseFloat(transaction.bodhi_protect_price || 0);
              const refundableExclFees = Math.max(0, thisChargeRemaining - txnFees);
              return refundableExclFees > 0;
            })() && (
              <TouchableOpacity 
                style={{
                  alignItems: 'center',
                  justifyContent:'center',
                  paddingVertical: 3,
                  paddingHorizontal: 12,
                  borderRadius: 5,
                  borderWidth: 2,
                  borderColor: '#6c757d',
                  marginLeft: 13,
                }}
                onPress={()=>{
                  // Fees (Service Fee + Bodhi Protect) are tracked PER TRANSACTION on
                  // the payment row, so we pass THIS payment's own captured fees. The
                  // refund modal protects exactly those fees — no proportional math,
                  // no order-level double-counting.
                  const refundDetails = {
                    id: transaction.id,
                    reservation_id: reservationInfo.id,
                    old_refunded: parseFloat(transaction.refunded) || 0,
                    amount: transaction.amount,
                    payment_intent: transaction.payment_intent,
                    service_fee: parseFloat(transaction.service_fee || 0),
                    bodhi_protect_price: parseFloat(transaction.bodhi_protect_price || 0)
                  }
                  openRefundModal(refundDetails)
                }}
              >
                <View style={{flexDirection:'row', alignItems:'center'}}>
                  <FontAwesome5 name={'redo'} size={14} style={{marginRight:10, marginTop:1}}/>
                  <Text style={[styles.outlineBtnText]}>{"refund"}</Text>
                </View>
              </TouchableOpacity>
            )}
          </View>
          {/* Amount + per-transaction fee split, right-aligned */}
          {(() => {
            const serviceFee = parseFloat(transaction.service_fee || 0);
            const bodhiProtect = parseFloat(transaction.bodhi_protect_price || 0);
            return (
              <View style={{ alignItems: 'flex-end' }}>
                <View style={{ flexDirection: 'row' }}>
                  <Text style={{ fontWeight: '600' }}>{fmt(transaction.amount)}</Text>
                  {transaction.refunded > 0 && (
                    <Text style={{ color: '#6c757d' }}>{` (refunded: ${fmt(transaction.refunded)})`}</Text>
                  )}
                </View>
                {serviceFee > 0 && (
                  <Text style={{ color: '#DC3545', fontSize: 13, marginTop: 2 }}>
                    {`Service Fee: ${fmt(serviceFee)}`}
                  </Text>
                )}
                {bodhiProtect > 0 && (
                  <Text style={{ color: '#DC3545', fontSize: 13, marginTop: 2 }}>
                    {`Bodhi Protect: ${fmt(bodhiProtect)}`}
                  </Text>
                )}
              </View>
            );
          })()}
        </View>
      </View>
    ));
  };
  
  const fmt = (val: any) => parseFloat(val || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  // Pick the Stripe payment with the most refundable money left (amount minus prior
  // refunds minus its own non-refundable fees). Used to return a downsize credit.
  const findBestRefundable = () => {
    let best: any = null;
    let bestRefundable = 0;
    (transactionData || []).forEach((t: any) => {
      if (!t.method || t.method.toLowerCase() !== 'stripe') return;
      const remaining = Math.max(0, parseFloat(t.amount || 0) - (parseFloat(t.refunded) || 0));
      const fees = parseFloat(t.service_fee || 0) + parseFloat(t.bodhi_protect_price || 0);
      const refundable = Math.max(0, remaining - fees);
      if (refundable > bestRefundable) { bestRefundable = refundable; best = t; }
    });
    return { best, bestRefundable };
  };

  // Open the Stripe refund modal on the best transaction, prefilled with the credit
  // (paid − total), capped by that transaction's fee-protected refundable amount.
  const refundCredit = () => {
    const credit = Math.abs(reservationInfo?.remaining_balance ?? 0);
    const { best, bestRefundable } = findBestRefundable();
    if (!best || bestRefundable <= 0) return;
    openRefundModal({
      id: best.id,
      reservation_id: reservationInfo.id,
      old_refunded: parseFloat(best.refunded) || 0,
      amount: best.amount,
      payment_intent: best.payment_intent,
      service_fee: parseFloat(best.service_fee || 0),
      bodhi_protect_price: parseFloat(best.bodhi_protect_price || 0),
      prefill_amount: Math.min(credit, bestRefundable),
    });
  };

  const renderSummaryRow = (label: string, value: string, bold?: boolean, topBorder?: boolean) => (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 5, ...(topBorder ? { borderTopWidth: 1, borderTopColor: '#ccc', marginTop: 4, paddingTop: 8 } : {}) }}>
      <Text style={{ fontSize: bold ? 15 : 14, color: '#333', fontWeight: bold ? '700' : '400' }}>{label}</Text>
      <Text style={{ fontSize: bold ? 15 : 14, color: '#333', fontWeight: bold ? '700' : '400' }}>{value}</Text>
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Header: Total / Paid / Add transaction */}
      <View style={{flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:6, flexWrap:'wrap-reverse'}}>
        <View style={{marginBottom:12}}>
          <View style={{flexDirection:'row', alignItems:'center', marginBottom:4}}>
            <Text style={{fontSize:16, marginRight:8}}>Total</Text>
            <Text style={{fontSize:16, marginRight:60}}>
              {fmt(reservationInfo?.total_price)}
            </Text>
            <Text style={{fontSize:16, marginRight:8}}>Paid</Text>
            <Text style={{fontSize:16}}>{fmt(reservationInfo?.paid)}</Text>
            {Number(reservationInfo?.total_price) > 0 &&
              Number(reservationInfo?.paid ?? 0) >= Number(reservationInfo?.total_price) && (
                <FontAwesome5 name="check-circle" solid size={16} color="#28A745" style={{marginLeft:8}} />
              )}
          </View>
          {(reservationInfo?.remaining_balance ?? 0) !== 0 && (
            <View style={{flexDirection:'row', alignItems:'center'}}>
              <Text style={{fontSize:15, marginRight:8, fontWeight:'600'}}>
                {(reservationInfo?.remaining_balance ?? 0) > 0 ? 'Remaining' : 'Credit'}
              </Text>
              <Text style={{fontSize:15, fontWeight:'600', color: (reservationInfo?.remaining_balance ?? 0) > 0 ? '#DC3545' : '#F0A500'}}>
                {fmt(Math.abs(reservationInfo?.remaining_balance ?? 0))}
              </Text>
              {(reservationInfo?.remaining_balance ?? 0) < 0 && findBestRefundable().bestRefundable > 0 && (
                <TouchableOpacity
                  onPress={refundCredit}
                  style={{ marginLeft: 12, paddingVertical: 3, paddingHorizontal: 12, borderRadius: 5, borderWidth: 2, borderColor: '#F0A500' }}
                >
                  <View style={{flexDirection:'row', alignItems:'center'}}>
                    <FontAwesome5 name={'undo'} size={12} color="#F0A500" style={{marginRight:8}} />
                    <Text style={{color:'#F0A500', fontWeight:'600'}}>{'Refund credit'}</Text>
                  </View>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>
        <TouchableOpacity style={[styles.outLineButton, {borderColor:'#28A745'}]} onPress={()=>{openAddTransactionModal()}}>
          <View style={{flexDirection:'row'}}>
            <FontAwesome5 name={'plus'} size={14} color="#28A745" style={{marginRight:10, marginTop:3}}/>
            <Text style={[styles.outlineBtnText, {color:'#28A745'}]}>Add transaction</Text>
          </View>
        </TouchableOpacity>
      </View>

      {/* Payment breakdown summary */}
      <View style={{ backgroundColor: (Number(reservationInfo?.total_price) > 0 && Number(reservationInfo?.paid ?? 0) >= Number(reservationInfo?.total_price)) ? '#e7f6ec' : '#fdecea', borderRadius: 6, paddingHorizontal: 14, paddingVertical: 8, marginBottom: 12, marginHorizontal: 6 }}>
        {renderSummaryRow('Subtotal', fmt(reservationInfo?.subtotal))}
        {reservationInfo?.service_fee != null && reservationInfo.service_fee > 0 &&
          renderSummaryRow('Service Fee', fmt(reservationInfo.service_fee))}
        {renderSummaryRow('Discount', fmt(reservationInfo?.discount_amount))}
        {reservationInfo?.bodhi_protect_price != null && reservationInfo.bodhi_protect_price > 0 &&
          renderSummaryRow('Bodhi Protect', fmt(reservationInfo.bodhi_protect_price))}
        {renderSummaryRow(`Tax (${reservationInfo?.tax_rate ?? 0}%)`, fmt(reservationInfo?.tax_amount))}
        {reservationInfo?.driver_tip > 0 &&
          renderSummaryRow('Tip', fmt(reservationInfo.driver_tip))}
        {renderSummaryRow('Total', fmt(reservationInfo?.total_price), true, true)}
      </View>

      {/* Transaction list */}
      <ScrollView contentContainerStyle={{paddingHorizontal:6, height:180}}>
        <View>
          {renderItems()}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingVertical: 18,
    // paddingHorizontal: 2,
    width:'100%',
    height:'100%',
  },
  transactionItem: {
    marginBottom: 12,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 5,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.2,
    shadowRadius: 3.84,
    elevation: 5,
  },
  outLineButton: {
    paddingVertical: 6,
    paddingHorizontal: 20,
    // alignItems: 'center',
    // justifyContent: 'center',
    borderRadius: 5,
    borderWidth: 2,
    borderColor: '#6c757d',
    marginBottom:12,
  },
  outlineBtnText: {
    fontSize: 14,
    letterSpacing: 1,
    textAlign: 'center',
  },
  summaryBox: {
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
    marginBottom: 12,
    marginHorizontal: 6,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
  },
  summaryRowBorder: {
    borderTopWidth: 1,
    borderTopColor: '#ccc',
    marginTop: 4,
    paddingTop: 8,
  },
  summaryLabel: {
    fontSize: 14,
    color: '#333',
  },
  summaryValue: {
    fontSize: 14,
    color: '#333',
  },
  summaryBold: {
    fontWeight: '700',
    fontSize: 15,
  },
});

export default TransactionList;
